/**
 * PromoteToKBModal
 * 
 * Shared modal component for promoting AI chatbot responses to the Knowledge Base.
 * Used by both KnowledgeManagementPage and ChatbotAdminPage.
 * 
 * Features:
 * - Auto-suggests category based on response content
 * - Auto-generates keywords based on response content and category
 * - Title Case formatting for categories
 * - All categories available with proper display names
 */

import React, { useState } from 'react';

// Helper function to convert string to Title Case
export const toTitleCase = (str) => {
  if (!str) return '';
  return str
    .split(/[-_\s]+/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// Helper function to auto-suggest category based on text content
export const suggestCategory = (text) => {
  if (!text) return 'general';
  
  const lowerText = text.toLowerCase();
  
  // Category detection patterns (ordered by specificity)
  const categoryPatterns = [
    { category: 'troubleshooting', patterns: ['error', 'issue', 'problem', 'fix', 'not working', 'broken', 'fail', 'crash', 'bug', 'troubleshoot'] },
    { category: 'getting-started', patterns: ['how to start', 'getting started', 'begin', 'first time', 'new user', 'onboarding', 'setup', 'introduction'] },
    { category: 'email-marketing', patterns: ['email', 'campaign', 'sendgrid', 'newsletter', 'marketing email', 'segment', 'subscriber', 'unsubscribe'] },
    { category: 'reports', patterns: ['report', 'analytics', 'metrics', 'dashboard', 'chart', 'graph', 'statistics', 'export report', 'sales report'] },
    { category: 'orders', patterns: ['order', 'invoice', 'shipment', 'apparelmagic', 'tracking', 'order status', 'purchase'] },
    { category: 'opportunities', patterns: ['opportunity', 'pipeline', 'deal', 'prospect', 'lead', 'sales stage', 'win rate'] },
    { category: 'customers', patterns: ['customer', 'client', 'account', 'company profile', 'customer record', 'buyer'] },
    { category: 'contacts', patterns: ['contact', 'person', 'email address', 'phone number', 'contact info'] },
    { category: 'tasks', patterns: ['task', 'todo', 'reminder', 'follow up', 'assign', 'due date', 'overdue'] },
    { category: 'administration', patterns: ['admin', 'user management', 'permission', 'role', 'settings', 'configuration', 'access'] },
    { category: 'navigation', patterns: ['navigate', 'find', 'where is', 'how do i get to', 'menu', 'sidebar', 'page'] },
    { category: 'features', patterns: ['feature', 'function', 'capability', 'tool', 'option'] },
    { category: 'tips', patterns: ['tip', 'best practice', 'shortcut', 'trick', 'efficient', 'quick way'] }
  ];
  
  // Count matches for each category
  const scores = categoryPatterns.map(({ category, patterns }) => {
    let score = 0;
    patterns.forEach(pattern => {
      const regex = new RegExp(pattern, 'gi');
      const matches = lowerText.match(regex);
      if (matches) {
        score += matches.length;
      }
    });
    return { category, score };
  });
  
  // Sort by score descending
  const sortedScores = [...scores].sort((a, b) => b.score - a.score);
  const best = sortedScores[0];
  const secondBest = sortedScores[1];
  
  // If no matches at all, return general
  if (best.score === 0) return 'general';
  
  // Count how many categories have significant matches (score > 0)
  const categoriesWithMatches = scores.filter(s => s.score > 0).length;
  
  // If content spans 3+ categories, it's likely a general/overview topic
  if (categoriesWithMatches >= 3) {
    // Check if any single category is clearly dominant (2x the second best)
    if (best.score >= secondBest.score * 2) {
      return best.category;
    }
    // Otherwise, it's a general topic covering multiple areas
    return 'general';
  }
  
  // If two categories have very similar scores (within 1 point), might be general
  if (categoriesWithMatches === 2 && secondBest.score > 0 && best.score - secondBest.score <= 1) {
    // Close tie between two categories - could go either way, pick the best
    return best.category;
  }
  
  return best.category;
};

// Helper function to extract keywords from text
export const extractKeywords = (text, category) => {
  if (!text) return [];
  
  // Common stop words to exclude
  const stopWords = new Set([
    'a', 'an', 'the', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with',
    'by', 'from', 'is', 'are', 'was', 'were', 'be', 'been', 'being', 'have', 'has', 'had',
    'do', 'does', 'did', 'will', 'would', 'could', 'should', 'may', 'might', 'must',
    'shall', 'can', 'need', 'this', 'that', 'these', 'those', 'i', 'you', 'he', 'she',
    'it', 'we', 'they', 'what', 'which', 'who', 'whom', 'when', 'where', 'why', 'how',
    'all', 'each', 'every', 'both', 'few', 'more', 'most', 'other', 'some', 'such',
    'no', 'nor', 'not', 'only', 'own', 'same', 'so', 'than', 'too', 'very', 'just',
    'also', 'now', 'here', 'there', 'then', 'once', 'if', 'your', 'our', 'their',
    'click', 'go', 'see', 'use', 'using', 'used', 'page', 'button', 'select', 'enter'
  ]);
  
  // CRM-specific important terms to boost
  const crmTerms = new Set([
    'customer', 'customers', 'order', 'orders', 'opportunity', 'opportunities',
    'task', 'tasks', 'report', 'reports', 'dashboard', 'sales', 'revenue',
    'commission', 'commissions', 'contact', 'contacts', 'company', 'companies',
    'email', 'marketing', 'campaign', 'segment', 'export', 'import', 'sync',
    'filter', 'search', 'create', 'edit', 'delete', 'view', 'list', 'add',
    'sofibella', 'crm', 'admin', 'settings', 'user', 'users', 'role', 'permission'
  ]);
  
  // Extract words, clean and filter
  const words = text.toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter(word => word.length > 2 && !stopWords.has(word));
  
  // Count word frequency
  const wordFreq = {};
  words.forEach(word => {
    wordFreq[word] = (wordFreq[word] || 0) + 1;
  });
  
  // Score words: frequency + CRM term bonus
  const scored = Object.entries(wordFreq).map(([word, freq]) => ({
    word,
    score: freq + (crmTerms.has(word) ? 3 : 0)
  }));
  
  // Sort by score and take top keywords
  const topKeywords = scored
    .sort((a, b) => b.score - a.score)
    .slice(0, 6)
    .map(item => item.word);
  
  // Add category as a keyword if not already present
  const categoryKeyword = category?.toLowerCase();
  if (categoryKeyword && categoryKeyword !== 'general' && !topKeywords.includes(categoryKeyword)) {
    topKeywords.unshift(categoryKeyword);
  }
  
  return topKeywords.slice(0, 8);
};

// Standard categories with proper display names
const STANDARD_CATEGORIES = [
  { value: 'general', label: 'General' },
  { value: 'customers', label: 'Customers' },
  { value: 'orders', label: 'Orders' },
  { value: 'opportunities', label: 'Opportunities' },
  { value: 'tasks', label: 'Tasks' },
  { value: 'reports', label: 'Reports' },
  { value: 'navigation', label: 'Navigation' },
  { value: 'features', label: 'Features' },
  { value: 'administration', label: 'Administration' },
  { value: 'contacts', label: 'Contacts' },
  { value: 'email-marketing', label: 'Email Marketing' },
  { value: 'getting-started', label: 'Getting Started' },
  { value: 'tips', label: 'Tips' },
  { value: 'troubleshooting', label: 'Troubleshooting' }
];

const STANDARD_CATEGORY_VALUES = STANDARD_CATEGORIES.map(c => c.value);

const PromoteToKBModal = ({ feedback, categories = [], onClose, onPromote }) => {
  const [title, setTitle] = useState(feedback.userQuestion || '');
  const [saving, setSaving] = useState(false);
  
  // Auto-suggest category based on content
  const [category, setCategory] = useState(() => {
    const combinedText = `${feedback.userQuestion || ''} ${feedback.assistantResponse || ''}`;
    return suggestCategory(combinedText);
  });
  
  // Auto-generate keywords based on response and category
  const [keywords, setKeywords] = useState(() => {
    const combinedText = `${feedback.userQuestion || ''} ${feedback.assistantResponse || ''}`;
    const suggestedCategory = suggestCategory(combinedText);
    const autoKeywords = extractKeywords(combinedText, suggestedCategory);
    return autoKeywords.join(', ');
  });
  
  // Update keywords when category changes
  const handleCategoryChange = (newCategory) => {
    setCategory(newCategory);
    // Regenerate keywords with new category
    const autoKeywords = extractKeywords(
      `${feedback.userQuestion || ''} ${feedback.assistantResponse || ''}`,
      newCategory
    );
    setKeywords(autoKeywords.join(', '));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    
    setSaving(true);
    try {
      await onPromote(feedback, title, category, keywords);
    } finally {
      setSaving(false);
    }
  };

  // Get custom categories not in standard list
  const customCategories = categories.filter(c => !STANDARD_CATEGORY_VALUES.includes(c));

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        <div className="p-6 border-b border-gray-200 bg-emerald-50">
          <h2 className="text-xl font-bold text-gray-900">Promote AI Response to Knowledge Base</h2>
          <p className="text-sm text-gray-600 mt-1">
            Create a new Knowledge Base article from this well-received AI response
          </p>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto max-h-[60vh]">
          {/* Original Question */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Original User Question</label>
            <div className="text-sm bg-gray-100 rounded p-3 text-gray-700">
              {feedback.userQuestion}
            </div>
          </div>
          
          {/* AI Response Preview */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">AI Response (will become article content)</label>
            <div className="text-sm bg-emerald-50 border border-emerald-200 rounded p-3 max-h-40 overflow-y-auto text-gray-700">
              {feedback.assistantResponse}
            </div>
          </div>
          
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Article Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              placeholder="Enter a clear, descriptive title..."
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              This will be used as the article title and for search matching
            </p>
          </div>
          
          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category
              <span className="text-xs font-normal text-emerald-600 ml-2">(auto-suggested based on content)</span>
            </label>
            <select
              value={category}
              onChange={(e) => handleCategoryChange(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
            >
              {STANDARD_CATEGORIES.map(cat => (
                <option key={cat.value} value={cat.value}>{cat.label}</option>
              ))}
              {customCategories.map(c => (
                <option key={c} value={c}>{toTitleCase(c)}</option>
              ))}
            </select>
          </div>
          
          {/* Keywords */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Keywords
              <span className="text-xs font-normal text-emerald-600 ml-2">(auto-generated, feel free to edit)</span>
            </label>
            <input
              type="text"
              value={keywords}
              onChange={(e) => setKeywords(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
              placeholder="keyword1, keyword2, keyword3..."
            />
            <p className="text-xs text-gray-500 mt-1">
              Comma-separated keywords to help users find this article
            </p>
          </div>
        </form>
        
        <div className="p-6 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            disabled={saving}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!title.trim() || saving}
            className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Promoting...
              </>
            ) : (
              '✨ Promote to Knowledge Base'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PromoteToKBModal;
